// src/services/historySync.js
//
// 🟢 HISTORY SYNC (web side) — pull the FULL, already-decrypted chat history from the
// phone, the way WhatsApp does on multi-device link.
//
// The web can't E2EE-decrypt messages sent BEFORE it paired (they weren't sealed to
// this browser). So instead of guessing, we ask the phone: it holds the plaintext in
// SQLite. On request the phone re-encrypts a history export SPECIFICALLY to this web
// device's public key (nacl.box) and uploads the ciphertext to Storage. We download +
// decrypt with our private key → every chat, every message, every type, no lock, no
// broken tiles. Media entries carry the cloud URL + symmetric key so we fetch+decrypt
// the same R2 blob on demand.
//
// 🟥 THE TRANSPORT CHANGED ON THE PHONE AND THIS FILE NEVER FOLLOWED, which is the
//    whole reason "Waiting for your phone…" could sit there forever while the phone
//    had answered perfectly. The export used to be one blob in Firebase Storage; a
//    Storage bucket has NO CORS rules until somebody sets them, and this one never
//    had any, so every download died in the preflight. The phone was moved to
//    Firestore chunk documents — Firestore's transport is CORS-enabled by design —
//    and it now writes `storagePath: null` explicitly, to "retire any path left by
//    an older build". This reader's first line was `if (!data.storagePath) return`.
//    So it skipped every single ready response, silently, by design, forever.
//
// Protocol (current — `transport: 'firestore-chats'`):
//   web  → users/{uid}/web_sync_requests/{deviceId} = { deviceId, publicKey, requestedAt, status:'pending' }
//   phone→ writes sibling docs `{reqId}__c{n}` = { chunkOf, chatId, part, parts, i, data }
//          where `data` is base64 of nacl.box( JSON(messages), nonce, webPub, phoneSecret )
//          sealed PER CHAT, then sets the request doc:
//            { status:'ready', transport:'firestore-chats', manifest:[{chatId,parts,nonce}],
//              chunks, senderDeviceId, storagePath:null, nonce:null }
//   web  → reads the chunks, reassembles per the manifest, opens each chat's box.
//
//   The legacy Storage shape is still honoured when `storagePath` is present, so an
//   older phone build keeps working.

import {
  doc, setDoc, onSnapshot, serverTimestamp, collection, getDocs, query, where,
} from 'firebase/firestore';
import { orderMessages } from './messageOrder';
import { ref, getBlob } from 'firebase/storage';
import nacl from 'tweetnacl';
import util from 'tweetnacl-util';
import { db, storage } from '../config/firebase';
import { getIdentity } from '../crypto/identity';
import { getDeviceKeys } from '../crypto/e2ee';
import { deviceLabel } from './session';
import { isShared } from './sharedMachine';
import { ensureFresh } from './plaintextGuard';
import { canSeal, sealJson, openJson } from '../crypto/vault';

const DB = 'allo_web_history';
const STORE = 'chats';

function idb() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE);
    r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error);
  });
}
async function rawGet(key) { const d = await idb(); return new Promise((res) => { const t = d.transaction(STORE, 'readonly').objectStore(STORE).get(key); t.onsuccess = () => res(t.result ?? null); t.onerror = () => res(null); }); }
async function rawSet(key, val) { const d = await idb(); return new Promise((res) => { const t = d.transaction(STORE, 'readwrite').objectStore(STORE).put(val, key); t.onsuccess = res; t.onerror = res; }); }

/* 🟢 SEALED WHEN THERE IS A KEY IN HAND, PLAINTEXT WHEN THERE IS NOT — and the
   record says which, so nothing has to guess. A browser with no vault keeps
   exactly the behaviour it had, protected by the 24-hour wipe; one that has
   enrolled writes ciphertext from that moment on.

   🟥 `canSeal`, NOT `isEnrolled`. The question is whether we may write sealed
      bytes, and that is true only while the key is actually in memory — sealing
      with a null key is how a cache fills with records nothing can ever open. */
async function cacheGet(key) {
  const rec = await rawGet(key);
  if (!rec || rec.enc !== 1) return rec;          // never enrolled, or written before enrolment
  /* 🟥 A LOCKED VAULT MUST NOT LOOK LIKE AN EMPTY CACHE. Returning null here
     would tell every layer above that the history is gone, and they would
     answer by asking the phone for all of it again — on a browser that already
     has it and merely cannot read it this second. */
  return openJson(rec);
}
async function cacheSet(key, val) {
  return rawSet(key, canSeal() ? await sealJson(val) : val);
}

/* ═══════════════════════════════════════════════════════════════════════════
   🟥 THE STATE MACHINE, AND WHY IT NEEDED ONE.

   The old version had four independent pieces of mutable state and no notion of
   WHICH ask any of them belonged to. That produced four distinct ways for the
   browser to lie about a phone that was working perfectly:

     1. STATUS COULD RUN BACKWARDS. `ask()` emitted 'requested' AFTER awaiting
        its own write. A phone that answered while that write was still in
        flight produced downloading → synced → **requested**, and since nothing
        else ever emits, "Waiting for your phone…" sat on top of a completed
        sync until the next ask. Permanently, for a whole minute at a time.

     2. EVERY BACKGROUND REFRESH ANNOUNCED ITSELF. A refresh fires on focus, on
        visibilitychange and on a heartbeat — and each one re-ran the full
        ladder in the pill even though a complete history was already on screen.
        That is the flash the user sees: waiting → downloading → gone → waiting.
        A refresh of something you already have has nothing to say. It is silent
        now, and only a first fill or an explicit Retry is allowed to speak.

     3. `answered` WAS SET ONCE AND NEVER RESET, so after the first success the
        stall detector could never fire again: a genuinely unanswered re-ask sat
        on "Waiting for your phone…" for ever with no escalation and no retry.

     4. THE PHONE'S OWN ANSWERS WERE THROWN AWAY. The handler opened with
        `if (data.status !== 'ready') return;`, which discards the export
        PROGRESS the phone writes every ~5% and the `status:'error'` it writes
        when a build fails. So a phone mid-export and a phone on fire both
        rendered as "waiting", and the percentage and progress bar in the
        sidebar were unreachable code.

   Everything below is keyed to a generation. One ask, one generation; anything
   arriving for an older one is dropped rather than allowed to repaint. Within a
   generation the ladder only ever climbs.
   ═══════════════════════════════════════════════════════════════════════════ */

/** How far along a generation is. Only ever climbs; a lower rung cannot repaint. */
const RANK = { requested: 0, acked: 1, building: 2, downloading: 3, opening: 4, synced: 5 };

/* ═══════════════════════════════════════════════════════════════════════════
   THE TWO RULES THAT DECIDE WHETHER TO ASK, AND WHETHER TO SAY SO.

   🟢 UP HERE, AND PURE, because both are the whole of a bug fix and a rule that
      cannot be run on its own is a rule nobody can check. Everything else in
      requestHistory is a listener, a timer or a write.
   ═══════════════════════════════════════════════════════════════════════════ */

/** How old an export on screen may be before a refresh of it counts as news. */
export const STALE_AFTER_MS = 10 * 60 * 1000;

/** The stamps come from Firestore's clock and `Date.now()` from this one. */
export const SKEW_MS = 30 * 1000;

/**
 * 🟥 `haveHistory` IS A BOOLEAN AND THE QUESTION IS NOT. "A refresh of something
 *    you already have is not news" is true of a five-second-old export and false
 *    of a twelve-hour-old one, and nothing recorded which of those was on screen
 *    — so a browser left open all day reloaded, served last morning's history,
 *    and refreshed in complete silence.
 *
 * 🟢 AN UNKNOWN AGE IS STALE. An export cached by a build that stored no
 *    timestamp reads as 0 here, and speaking once too often is the harmless
 *    direction of that error.
 */
export function exportIsStale(shownAt, now = Date.now()) {
  return !shownAt || (now - shownAt) > STALE_AFTER_MS;
}

/**
 * Is the chat list describing something the export on screen cannot explain?
 * @returns the activity stamp to ask about, or 0 for "nothing to ask".
 *
 * 🟥 A 1:1 MESSAGE DOCUMENT IS DELETED BY STORE-AND-FORWARD, so a day of texting
 *    marches every chat document's `timestamp` forward with nothing readable
 *    behind it. That is observable — the list SORTS by that field — and until
 *    now nothing did anything with it.
 *
 * 🟥 AND EACH ADVANCE IS ASKED ABOUT ONCE. A group call that ends stamps the
 *    chat document and writes no message at all: an activity time no export will
 *    ever explain. Without `noted`, that one stamp would ask again on every
 *    snapshot for ever. Remembering what was asked about turns "the list is
 *    ahead" into "the list has moved since I asked", which is the question that
 *    terminates.
 */
export function askAbout(activityMs, shownAt, noted) {
  const t = Number(activityMs) || 0;
  if (!t) return 0;
  if (t <= (Number(noted) || 0)) return 0;
  if (t <= (Number(shownAt) || 0) + SKEW_MS) return 0;
  return t;
}

/**
 * Kick off (or refresh) a history request and stream the result.
 *   onHistory({chatId: [msgs...]})
 *   onStatus(status, progress?)
 * @returns {{stop: function, retry: function}}
 */
/** The history equivalent of mediaStore.unsealAll — see the note there. */
export async function unsealAllHistory() {
  if (!canSeal()) throw new Error('locked');
  const d = await idb();
  const keys = await new Promise((res) => {
    const t = d.transaction(STORE, 'readonly').objectStore(STORE).getAllKeys();
    t.onsuccess = () => res(t.result || []); t.onerror = () => res([]);
  });
  let done = 0;
  for (const k of keys) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const rec = await rawGet(k);
      if (!rec || rec.enc !== 1) continue;
      // eslint-disable-next-line no-await-in-loop
      await rawSet(k, await openJson(rec));
      done += 1;
    } catch { /* skip the unreadable, free the rest */ }
  }
  return done;
}

export async function requestHistory(userId, onHistory, onStatus) {
  /* 🟥 A SHARED MACHINE MUST NOT BE HANDED THE ARCHIVE. This function's whole
     job is to pull the account's history out of the phone and write it to this
     disk — which is the single worst thing to do on a computer somebody else
     will use next. Live messages still arrive over the socket and are wiped
     with the tab; it is the durable copy that is refused. */
  if (isShared()) { onStatus?.('shared_machine'); return () => {}; }
  /* 🟥 BEFORE THE CACHE IS READ, NOT ALONGSIDE IT. This function serves the
     stored export instantly by design, so a guard that merely ran at startup
     would lose the race on a slow boot and paint a day-old archive a moment
     before deleting it. Awaited here, the only reader of the archive cannot
     read a stale one. */
  await ensureFresh();
  const me = await getIdentity();

  let gen = 0;             // the ask everything is measured against
  let rank = -1;           // how far THIS generation has climbed
  let loud = true;         // may this generation paint anything at all?
  let askId = '';          // this generation's identity on the wire
  let haveHistory = false; // is there something readable on screen right now?
  let stopped = false;

  /**
   * 🟥 THE ONLY WAY A STATUS REACHES THE UI. Three gates, and each one exists
   *    because of a specific way the old code lied:
   *      · a stale generation cannot repaint (fault 1)
   *      · a silent generation cannot paint at all (fault 2)
   *      · a lower rung cannot overwrite a higher one (fault 1 again, within
   *        one generation — progress writes and the ready write race)
   */
  const emit = (g, status, progress) => {
    if (stopped || g !== gen) return false;
    if (!loud) return false;
    const r = RANK[status];
    if (r !== undefined) {
      if (r < rank) return false;
      /* 🟢 THE SAME RUNG TWICE IS ONLY NEWS IF IT CARRIES A NUMBER. The phone
         re-announces its ack on every snapshot of the same request, and the
         export writes progress many times over — the first must not repaint,
         the second must. A payload is exactly what separates them. */
      if (r === rank && !progress) return false;
      rank = r;
    }
    onStatus?.(status, progress);
    return true;
  };

  /* ═══════════════════════════════════════════════════════════════════════
     🟥 THE CACHE HAD NO AGE, SO "I ALREADY HAVE IT" COULD NOT BE QUESTIONED.

     The rule below — a refresh of something you already have is not news — is
     right for a five-second-old export and wrong for a twelve-hour-old one, and
     `haveHistory` is a BOOLEAN: it cannot tell those apart. So the browser you
     left open all day, reloaded at night, served last morning's history and then
     refreshed in complete silence. No pill, and no "Fetching from your phone…"
     on the rows either, because that line only appears on a row with NO text.

     A timestamp beside the cache is the whole distinction. It is stored under
     its own key rather than inside the payload, so an export cached by an older
     build still loads — it simply reads as "age unknown", which is treated as
     stale, which is the safe direction. The rule itself is exportIsStale, at
     the top of this file, so it can be run on its own.
     ═══════════════════════════════════════════════════════════════════════ */

  // serve the cached export instantly, then refresh from the phone
  const cached = await cacheGet(`hist_${userId}`);
  const cachedAt = Number(await cacheGet(`histAt_${userId}`)) || 0;
  /* 🟢 AN EMPTY CACHE IS NOT A CACHE. `{}` is what a first-ever load or a failed
     reassembly leaves behind, and treating it as "history on screen" would make
     the first real sync silent — the one case that genuinely needs to speak. */
  /** When the history currently ON SCREEN was built. 0 = nothing, or age unknown. */
  let shownAt = 0;
  if (cached && Object.keys(cached).length) {
    haveHistory = true;
    shownAt = cachedAt;
    onStatus?.('cached');
    onHistory?.(cached);
  }
  const shownIsStale = () => exportIsStale(shownAt);

  /* 🟢 THE STALL THRESHOLD IS ABOUT THE PHONE, NOT ABOUT THE EXPORT. The phone
     now acks a request the instant it sees it — before it decides whether to
     throttle, before it starts building — so silence for this long really does
     mean nothing is listening. Once acked the clock is cancelled outright: an
     export on a large account legitimately runs for minutes, and the old code
     called that a dead phone at 25 seconds.
     An older phone build writes no ack, so this stays at the horizon it always
     had and that build behaves exactly as it used to. */
  const STALL_AFTER = 25000;
  let stallTimer = null;
  const clearStall = () => { if (stallTimer) { clearTimeout(stallTimer); stallTimer = null; } };
  const armStall = (g) => {
    clearStall();
    stallTimer = setTimeout(() => { emit(g, 'stalled'); }, STALL_AFTER);
  };

  /* 🟥 THE ASK CARRIES ITS OWN IDENTITY. `requestedAt` is a serverTimestamp:
     a sentinel locally, second-granular once resolved, and therefore useless
     both for matching an ack to the ask that earned it and for telling two
     asks in the same second apart — the phone keyed its de-duplication on it
     and silently dropped the second of any such pair. A client-side id has
     neither problem and needs no clock to agree with any other clock. */
  const newAskId = () => `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

  const ask = async ({ loud: wantLoud }) => {
    gen += 1;
    rank = -1;
    loud = wantLoud;
    askId = newAskId();
    const g = gen;
    /* 🟢 SAID BEFORE THE WRITE, NOT AFTER IT. This is fault 1: the await used to
       sit between the intention and the announcement, so a fast phone could
       finish the whole exchange before the browser got round to saying it had
       started — and then the announcement landed on top of the result. */
    emit(g, 'requested');
    armStall(g);
    try {
      await setDoc(doc(db, 'users', userId, 'web_sync_requests', me.deviceId), {
        deviceId: me.deviceId,
        publicKey: me.publicKey,
        askId,
        /* 🟢 SAY WHAT THIS BROWSER IS, so the phone's progress notification can
           name it. This is the most a browser truthfully knows about the machine
           it is on — "Chrome on macOS". A web page cannot read the computer's
           hostname, deliberately, so a friendlier name would be a guess. */
        deviceName: deviceLabel(),
        requestedAt: serverTimestamp(),
        status: 'pending',
      }, { merge: true });
      /* 🟢 SAY WHO IS ASKING. When this stalls, the question is always the same:
         is the phone signed in as the SAME account, and is it awake? These two
         values are the whole answer and cost one line to print. */
      console.log(`[historySync] asked as uid=${userId} device=${me.deviceId} ask=${askId}${wantLoud ? '' : ' (silent refresh)'}`);
    } catch (e) {
      console.warn('[historySync] request failed:', e?.code || e?.message, e);
      if (g === gen) { clearStall(); emit(g, 'error'); }
    }
  };

  /* 🟥 THE FIRST ASK SPEAKS IF THERE IS NOTHING TO LOOK AT — **OR IF WHAT THERE
     IS TO LOOK AT IS OLD**. That second clause is the missing one. With a cache
     on screen this used to be a refresh like any other, and "a refresh of
     something you already have is not news" is a statement about a CURRENT
     answer; a day-old export is not one. */
  await ask({ loud: !haveHistory || shownIsStale() });

  let lastAsk = Date.now();
  const RE_ASK_MIN_GAP = 60 * 1000;
  const maybeAsk = () => {
    if (stopped) return;
    if (Date.now() - lastAsk < RE_ASK_MIN_GAP) return;
    lastAsk = Date.now();
    ask({ loud: !haveHistory || shownIsStale() });
  };
  const onVis = () => { if (document.visibilityState === 'visible') maybeAsk(); };
  document.addEventListener('visibilitychange', onVis);
  window.addEventListener('focus', onVis);
  const heartbeat = setInterval(maybeAsk, 5 * 60 * 1000);

  /* ═══════════════════════════════════════════════════════════════════════
     🟥 THE MISSING EDGE. Everything above is a CLOCK — load, focus, and a
     five-minute heartbeat. None of them is the event that actually matters,
     which is "a conversation moved and this browser cannot see what moved it".

     That event is observable, and the chat list already has it. A 1:1 message
     document is deleted by store-and-forward on the receiving phone, so a whole
     day of texting can pass with the chat documents' `timestamp` marching
     forward and not one readable message behind it. The browser could see the
     rows reorder — it sorts by exactly that field — and did not think to ask.

     🟢 SO THE LIST TELLS THE SYNC WHAT IT SAW. Activity later than the export
        on screen is proof that a message exists which this browser has not been
        given, and proof is a better trigger than a timer.

     🟥 AND IT MUST ASK ABOUT EACH ADVANCE ONCE. A group call that ends stamps
        the chat document and writes no message at all — an activity time no
        export will ever explain. Without `noted` that single stamp would ask
        again on every snapshot, for ever. Remembering the value asked about
        turns "the list is ahead" into "the list has moved since I last asked",
        which is the question with an end to it.
     ═══════════════════════════════════════════════════════════════════════ */
  let noted = 0;
  let seenTimer = null;
  const seen = (activityMs) => {
    if (stopped) return;
    const t = askAbout(activityMs, shownAt, noted);
    if (!t) return;
    const wait = RE_ASK_MIN_GAP - (Date.now() - lastAsk);
    if (wait > 0) {
      /* 🟢 DEFERRED, NOT DROPPED, AND NOT STAMPED YET. Marking it noted here
         would answer a question we have not asked. One timer, replaced rather
         than stacked, so a burst of snapshots collapses into a single ask. */
      if (seenTimer) clearTimeout(seenTimer);
      seenTimer = setTimeout(() => { seenTimer = null; seen(t); }, wait + 250);
      return;
    }
    noted = t;
    lastAsk = Date.now();
    /* 🟢 LOUD. This ask exists because the browser knows it is behind, which is
       the one thing the person in front of it would want said out loud. */
    ask({ loud: true });
  };

  /* 🟢 THE ONE ASK THAT IS ALWAYS LOUD, AND ALWAYS ALLOWED. A person pressing
     Retry has told us they are waiting for it, so it ignores both the silence
     rule and the re-ask gap — those exist to keep the machinery quiet, not to
     refuse a direct instruction. */
  const retry = () => {
    if (stopped) return;
    lastAsk = Date.now();
    ask({ loud: true });
  };

  /**
   * The current shape: one sealed box PER CHAT, split across sibling chunk documents
   * and described by a manifest. Reassembled here.
   *
   * 🟢 THE MANIFEST IS THE CONTRACT, and it is what makes a short read detectable.
   *    Concatenating a hole would hand nacl a truncated box, which fails to open and
   *    looks exactly like "wrong key" — an hour of debugging the wrong thing. So a
   *    chat whose parts do not all arrive is SKIPPED and named in the console,
   *    rather than quietly corrupting the export.
   */
  async function readChunked(data, senderPub) {
    const reqCol = collection(db, 'users', userId, 'web_sync_requests');
    let docs = [];
    try {
      const snapChunks = await getDocs(query(reqCol, where('chunkOf', '==', me.deviceId)));
      docs = snapChunks.docs.map((d) => d.data());
    } catch (e) {
      // a missing index on `chunkOf` must not sink the sync — read them all and filter
      const all = await getDocs(reqCol);
      docs = all.docs.map((d) => d.data()).filter((d) => d && d.chunkOf === me.deviceId);
    }

    // chatId → part index → base64
    const byChat = new Map();
    for (const c of docs) {
      if (!c || !c.chatId || typeof c.data !== 'string') continue;
      if (!byChat.has(c.chatId)) byChat.set(c.chatId, new Map());
      byChat.get(c.chatId).set(Number(c.part) || 0, c.data);
    }

    const chats = {};
    let missing = 0;
    for (const entry of (data.manifest || [])) {
      const parts = byChat.get(entry.chatId);
      if (!parts || parts.size !== entry.parts) { missing += 1; continue; }
      let b64 = '';
      for (let i = 0; i < entry.parts; i++) b64 += parts.get(i) || '';
      const opened = nacl.box.open(
        util.decodeBase64(b64), util.decodeBase64(entry.nonce), senderPub, me.secretKeyU8,
      );
      if (!opened) { missing += 1; continue; }
      try { chats[entry.chatId] = JSON.parse(util.encodeUTF8(opened)); } catch { missing += 1; }
    }
    if (missing) console.warn(`[historySync] ${missing} of ${(data.manifest || []).length} chats could not be reassembled`);
    return chats;
  }

  /** The legacy shape: one blob in Firebase Storage. Kept for older phone builds. */
  async function readStorage(data, senderPub) {
    const blob = await getBlob(ref(storage, data.storagePath));
    const cipher = new Uint8Array(await blob.arrayBuffer());
    const opened = nacl.box.open(cipher, util.decodeBase64(data.nonce), senderPub, me.secretKeyU8);
    if (!opened) return null;
    return JSON.parse(util.encodeUTF8(opened)).chats || {};
  }

  // wait for the phone to publish the encrypted export
  const unsub = onSnapshot(doc(db, 'users', userId, 'web_sync_requests', me.deviceId), async (snap) => {
    if (stopped) return;
    const data = snap.data();
    if (!data) return;
    const g = gen;

    /* 🟢 IS THIS THE PHONE TALKING, OR OUR OWN WRITE COMING BACK? Firestore
       replays every local write to its own listener before the server has seen
       it. Reading our `status:'pending'` as though the phone had said something
       is harmless today but would make every field below ambiguous. */
    if (snap.metadata && snap.metadata.hasPendingWrites) return;

    /* ── 1. THE ACK: "a phone is here and it heard you" ──────────────────────
       This is the entire point of the round trip. Before it existed the browser
       could not tell a phone that was throttling, busy exporting, or mid-upload
       from a phone that was switched off — and it accused all four of not
       answering. An ack with no id is an older phone build; take it. */
    if (data.ackAt && (!data.ackAskId || data.ackAskId === askId)) {
      clearStall();
      emit(g, 'acked');
    }

    /* ── 2. THE PHONE'S OWN PROGRESS ──────────────────────────────────────────
       Written every ~5% of the export loop and, until now, read by nobody: the
       old handler returned above this on `status !== 'ready'`. This is why a
       long export looked identical to a dead phone. */
    const p = data.progress;
    if (p && Number(p.total) > 0 && (!p.askId || p.askId === askId)) {
      /* 🟢 CLAMPED ONCE, AT THE SOURCE. `done` is pinned inside [0, total], which
         is what makes the ratio a percentage — a second clamp on the result
         afterwards could never fire, and a guard that cannot fire is not a
         guard, it is a reader's false comfort. */
      const done = Math.max(0, Math.min(Number(p.total), Number(p.done) || 0));
      const pct = Math.round((done / Number(p.total)) * 100);
      clearStall();               // it is plainly alive; stop counting against it
      emit(g, 'building', { pct, text: 'Preparing on your phone…' });
    }

    /* ── 3. A BUILD THAT FAILED SAYS SO ──────────────────────────────────────
       Also swallowed by the old early return, so an export that threw on the
       phone was rendered as "still waiting", for ever. */
    if (data.status === 'error') { clearStall(); emit(g, 'error'); return; }
    /* ── 3b. 🔐 THE PHONE SAID NO, ON PURPOSE (its services/historyGate) ─────
       'declined': the person holding the phone tapped "Don't allow".
       'refused': this browser's key is not a registered device of the account.
       Either way it is an answer — not "your phone hasn't answered". */
    if ((data.status === 'declined' || data.status === 'refused') && (!data.refusedAskId || data.refusedAskId === askId)) {
      clearStall(); emit(g, 'declined'); return;
    }

    if (data.status !== 'ready') return;

    /* 🟢 WHOSE EXPORT IS THIS? A `ready` left over from the previous ask is
       still perfectly good history, but consuming it would resolve THIS
       generation with someone else's answer — and the export actually owed to
       this ask would then arrive with nowhere to land. An export with no id is
       an older phone build; take it, as before. */
    if (data.readyAskId && data.readyAskId !== askId) {
      console.log('[historySync] ignoring a ready from an earlier ask', data.readyAskId);
      return;
    }

    // 🟥 NO `storagePath` TEST HERE. The current phone sets it to null on purpose.
    const chunked = data.transport === 'firestore-chats' || Array.isArray(data.manifest);
    if (!chunked && !data.storagePath) {
      console.warn('[historySync] ready, but neither a manifest nor a storagePath — phone build too old or too new');
      clearStall();
      emit(g, 'error');
      return;
    }
    clearStall();
    try {
      emit(g, 'downloading', { text: 'Downloading history…' });

      const senderKeys = await getDeviceKeys(userId, true);
      const senderPubB64 = senderKeys[data.senderDeviceId];
      if (!senderPubB64) {
        console.warn('[historySync] no published key for the exporting device', data.senderDeviceId);
        emit(g, 'error'); return;
      }
      const senderPub = util.decodeBase64(senderPubB64);

      emit(g, 'opening');
      const chats = chunked ? await readChunked(data, senderPub) : await readStorage(data, senderPub);
      if (!chats) { emit(g, 'error'); return; }

      await cacheSet(`hist_${userId}`, chats);
      /* 🟢 WRITTEN BESIDE THE EXPORT, NOT DERIVED FROM IT. The newest message in
         an export is not the same thing as when the export was taken — an
         account with nothing new all week would look permanently stale. This is
         the moment the answer was known to be current. */
      await cacheSet(`histAt_${userId}`, Date.now());
      shownAt = Date.now();
      /* 🟥 BEFORE THE STATUS, NOT AFTER IT. `haveHistory` is what makes the NEXT
         refresh silent, and a refresh that fires between the emit and this line
         would wrongly decide it had nothing to show and start shouting again.
         🟥 AND UNCONDITIONALLY, NOT `if (chats.length)`. What this flag really
            means is "the user has been shown the current answer", and an account
            with no history yet has been shown it just as completely as one with
            two hundred chats. Gating on the count would leave a new account
            announcing "Waiting for your phone…" on every focus, for ever — the
            precise behaviour this whole change exists to remove. */
      haveHistory = true;
      emit(g, 'synced');
      onHistory?.(chats);
      console.log(`[historySync] synced ${Object.keys(chats).length} chats`);
    } catch (e) {
      console.warn('[historySync] download/decrypt failed:', e?.message);
      emit(g, 'error');
    }
  });

  const stop = () => {
    stopped = true;
    try { unsub(); } catch { /* already gone */ }
    document.removeEventListener('visibilitychange', onVis);
    window.removeEventListener('focus', onVis);
    clearInterval(heartbeat);
    clearStall();
    if (seenTimer) { clearTimeout(seenTimer); seenTimer = null; }
  };

  return { stop, retry, seen };
}

/* ═══════════════════════════════════════════════════════════════════════════
   🟥 WHICH DECODER READS THIS MESSAGE — AND WHY ASKING "IS IT ENCRYPTED" IS THE
      WRONG QUESTION.

   A media message reaches the browser in one of two shapes, and they use
   DIFFERENT FIELD NAMES for the same thing:

     · a live Firestore document  → `imageUrl` / `videoUrl` / `audioUrl` /
                                     `documentUrl`, `images[]`, and per-type
                                     sealed key maps
     · the phone's exported entry → `url`, `images[]`, and an already-unsealed
                                     `mediaKey` / `mediaKeys`

   The chat used to choose between them with `m._plain !== undefined ||
   m.mediaKey || m.mediaKeys` — a test of whether a KEY is present. That is
   correct for every sealed message and wrong for every plaintext one, because
   `exportMessage` writes `mediaKey: null` when there was nothing to unseal.

   🟥 SO AN UNENCRYPTED EXPORTED PHOTO WAS HANDED TO THE FIRESTORE DECODER, which
      looked for `imageUrl`, found nothing, and set no media at all — "Media
      unavailable", with a Try again that worked the moment the live document
      arrived and put `imageUrl` back within reach.

      The one chat in the product that is never encrypted is the official Allo
      account, which is exactly where it was reported, every time. The rare other
      cases are the other plaintext senders: legacy unsealed rows and forwards.

   🟢 THE MARKER IS THE FIELD, NOT ITS VALUE. `exportMessage` ALWAYS writes one
      of `mediaKey` / `mediaKeys` on a media entry — null when the message was
      plaintext — so its PRESENCE identifies an export entry exactly, with no
      change needed on the phone and no guessing about older builds.
   ═══════════════════════════════════════════════════════════════════════════ */
export function isExportedEntry(m) {
  if (!m) return false;
  if (m._plain !== undefined) return true;
  return Object.prototype.hasOwnProperty.call(m, 'mediaKey')
    || Object.prototype.hasOwnProperty.call(m, 'mediaKeys');
}

/** Live fields worth taking from Firestore even when we keep the phone's copy of the
 *  content: receipts and state, none of which the export can know about. */
const LIVE_STATE = ['sent', 'delivered', 'seen', 'reactions', 'deletedFor', 'isDeleted',
  'editedAt', 'seq', 'starred', 'pinned'];

/**
 * 🟥 CAN THIS BROWSER ACTUALLY READ THE LIVE COPY?
 *
 * A sealed message carries one copy per device, addressed by deviceId. If there is no
 * copy for us, the live doc is a padlock and nothing will ever open it — no retry, no
 * refresh, no amount of waiting. The phone's export, by contrast, is plaintext. So
 * "live wins" must not apply here: it would throw away the only readable version.
 */
/* 🟥 AND A PHOTO IS NOT SEALED UNDER `perDeviceKeys`. That field is the TEXT
   vocabulary. Media seals its symmetric key in a field of its own —
   imagePerDeviceKeys, videoPerDeviceKeys, audioPerDeviceKeys,
   documentPerDeviceKeys, batchPerDeviceKeys for an album, thumbPerDeviceKeys
   for the poster — and this function looked at none of them.

   THAT IS THE BROKEN-MEDIA REPORT, ENTIRELY. Asked about a photo sent before
   this browser re-linked, the old test found no `perDeviceKeys`, answered
   "readable", and let the live document win. Winning threw away the export's
   PLAINTEXT `mediaKey` / `mediaKeys` — the only copy this browser can open —
   and left it holding a padlock addressed to a device that no longer exists.

   It is also exactly why it flickers. The export is in memory and paints first,
   so the picture appears; the Firestore document lands a moment later, wins this
   merge, changes the row's media signature, and the media queue re-resolves
   against the copy it can never open. Under a second, and the photo breaks in
   front of you. Every symptom in the report is that one sequence: fine when sent
   from the app, broken after a logout and a re-link (a NEW device key — see
   wipeLocal.js, which drops allo_web_identity by design), an album whose first
   tile breaks while the rest spin, and perfectly fine on the phone, which has
   the plaintext in SQLite and never asks this question at all. */
const SEAL_FIELDS = [
  'perDeviceKeys',            // text, and a media caption
  'imagePerDeviceKeys', 'videoPerDeviceKeys', 'audioPerDeviceKeys',
  'documentPerDeviceKeys', 'batchPerDeviceKeys',
  'thumbPerDeviceKeys', 'docThumbPerDeviceKeys',
];

/** Is there a copy addressed to THIS device in one seal map? */
function sealedToMe(map, myDeviceId) {
  if (!map || typeof map !== 'object') return false;
  if (map[myDeviceId]) return true;
  // the phone also writes `${uid}:${deviceId}` keys — check that shape too
  return Object.keys(map).some((k) => k.endsWith(`:${myDeviceId}`));
}

function unreadableHere(m, myDeviceId) {
  if (!m || !myDeviceId) return false;
  /* 🟢 ANY seal this message carries that is not addressed to us makes the live
     copy the worse one. A message can carry two — a photo's caption under
     `perDeviceKeys` and its key under `imagePerDeviceKeys` — and after a re-link
     neither is ours. Requiring ALL of them to be unreadable would keep the live
     copy whenever one half happened to open, which is the half-broken bubble
     this is meant to prevent. The export has plaintext for both. */
  for (const f of SEAL_FIELDS) {
    const map = m[f];
    if (!map || typeof map !== 'object') continue;
    if (!Object.keys(map).length) continue;
    if (!sealedToMe(map, myDeviceId)) return true;
  }
  /* 🟥 AND A MESSAGE WITH NO SEALS AT ALL IS NOT UNREADABLE. An official-account
     photo is plaintext by design; answering "unreadable" for it would prefer a
     stale export over the live document for every unencrypted message there is,
     which is the opposite mistake and just as invisible. */
  return false;
}

/** Merge synced history for one chat with the live Firestore messages (dedupe by id).
 *  Pass myDeviceId so an unreadable live doc can't overwrite a readable exported one. */
/**
 * Drop one row from the archive — a delete-for-me whose document is already gone.
 *
 * 🟥 A LOCAL HIDE HAS NO DOCUMENT TO CARRY THE FLAG. Store-and-forward may have
 *    swept the message long before you deleted it, so nothing arrives through the
 *    live stream to say so and retainLive never sees it. Without this the row
 *    would be deleted on screen and still be in the archive, and the chat list
 *    would keep quoting it.
 */
export async function forgetRetained(userId, chatId, messageId) {
  if (!userId || !chatId || !messageId) return;
  try {
    const key = `hist_${userId}`;
    const store = (await cacheGet(key)) || {};
    const list = Array.isArray(store[String(chatId)]) ? store[String(chatId)] : null;
    if (!list) return;
    const next = list.filter((m) => String(m?.id) !== String(messageId));
    if (next.length === list.length) return;
    store[String(chatId)] = next;
    await cacheSet(key, store);
    for (const fn of _retainListeners) { try { fn(store); } catch { /* not our problem */ } }
  } catch { /* the on-screen hide already happened; this is the durable half */ }
}

/* ── what arrived live must survive leaving the room ─────────────────────────

   🟥 THE BROWSER HELD LIVE MESSAGES IN MEMORY ONLY, and store-and-forward deletes
      the documents they came from. So a message would appear the moment it
      arrived, and leaving the chat and coming back showed nothing: the live list
      was gone with the component, the Firestore doc had been swept, and the
      phone's export — the one durable copy — had been taken before the message
      existed. It came back "on the next re-sync" because that is literally the
      only thing that could bring it back.

   🟢 SO THE EXPORT IS APPENDED TO, NOT MERELY READ. The archive is this browser's
      durable store, so anything it can see belongs in it — a syncing pass is then
      what it should always have been: how a NEW browser catches up, not what
      every message is waiting for.

   🟥 UNION, NEVER REPLACE. This must not be able to remove or downgrade a row: a
      writer that can subtract is a writer that can lose the conversation, which
      is the bug it exists to fix. */
let _retainTimer = null;
const _retainListeners = new Set();
/** Told whenever the archive grows, with the whole store. */
export function onRetained(fn) {
  _retainListeners.add(fn);
  return () => _retainListeners.delete(fn);
}
const _retainQueue = new Map();   // chatId -> rows

export async function retainLive(userId, chatId, rows) {
  if (!userId || !chatId || !Array.isArray(rows) || !rows.length) return;
  _retainQueue.set(String(chatId), rows);
  /* 🟢 COALESCED. A busy chat would otherwise rewrite the whole archive on every
     keystroke's worth of traffic; a couple of seconds of lag costs nothing
     because the in-memory list is already on screen. */
  if (_retainTimer) return;
  _retainTimer = setTimeout(async () => {
    _retainTimer = null;
    const batch = [..._retainQueue.entries()];
    _retainQueue.clear();
    try {
      const key = `hist_${userId}`;
      const store = (await cacheGet(key)) || {};
      let touched = false;
      for (const [cid, list] of batch) {
        const prev = Array.isArray(store[cid]) ? store[cid] : [];
        const byId = new Map();
        for (const m of prev) if (m && m.id) byId.set(String(m.id), m);
        for (const m of list) {
          if (!m || !m.id) continue;
          const id = String(m.id);
          /* 🟥 "UNION, NEVER REPLACE" HAS EXACTLY ONE EXCEPTION, AND FORGETTING IT
             LEFT DELETED MESSAGES IN THE CHAT LIST FOR EVER. The rule exists so a
             writer cannot LOSE the conversation — but a deletion is not loss, it
             is an instruction, and an archive that cannot honour it keeps showing
             the row somebody removed. The phone's chat tab was right and the
             browser was not, for this reason. */
          if (m.isDeleted === true || m.deleted === true
              || (Array.isArray(m.deletedFor) && m.deletedFor.includes(String(userId)))) {
            if (byId.delete(id)) touched = true;
            continue;
          }
          const had = byId.get(id);
          /* 🟥 MERGED FIELD-WISE, so a live row that is missing something the
             archive already knew (a thumbnail, an unsealed caption) cannot blank
             it. Anything the live row actually carries still wins. */
          const next = had ? { ...had, ...m } : m;
          /* 🟢 AND ONLY A REAL CHANGE COUNTS AS ONE. Without this every render of
             a busy chat rewrites — and re-encrypts — the entire archive on a
             timer, for rows that are byte-identical to what is already there.
             New id, or a receipt that moved: those are the things worth a write. */
          if (!had || had.seen !== next.seen || had.delivered !== next.delivered
              || had.isDeleted !== next.isDeleted || had._updateTick !== next._updateTick) {
            touched = true;
          }
          byId.set(id, next);
        }
        store[cid] = Array.from(byId.values());
      }
      if (touched) {
        await cacheSet(key, store);
        /* 🟥 WRITING IT DOWN IS HALF THE JOB. The view reads `syncedHistory` from
           App state, which only changes when requestHistory runs — so a row saved
           to IndexedDB and nothing else is invisible until the next sync, which
           is the exact symptom this was meant to cure. Whoever holds that state
           has to be told the archive grew. */
        for (const fn of _retainListeners) { try { fn(store); } catch { /* a bad listener is not our problem */ } }
      }
    } catch { /* a failed retain is a re-sync, not a loss — the list is still on screen */ }
  }, 2500);
}

export function mergeChatHistory(liveMsgs, syncedForChat, myDeviceId) {
  if (!syncedForChat || !syncedForChat.length) return liveMsgs;
  const byId = new Map();
  for (const m of syncedForChat) byId.set(m.id || m.clientTempId, m);
  for (const m of liveMsgs) {
    const key = m.id || m.clientTempId;
    const synced = byId.get(key);

    // 🟥 THE REGRESSION THIS FIXES. Both copies exist for almost every recent message:
    //    the sealed Firestore doc and the phone's plaintext export. Live used to win
    //    unconditionally — so after this browser's key changed, every message that still
    //    had a Firestore doc rendered as "Sent before this device was linked" even though
    //    the readable copy was sitting right there in the export.
    if (synced && unreadableHere(m, myDeviceId)) {
      const state = {};
      for (const k of LIVE_STATE) if (m[k] !== undefined) state[k] = m[k];
      if (m.linkMetadata) state.linkMetadata = m.linkMetadata;   // plaintext on both clients
      byId.set(key, {
        ...synced,
        ...state,
        ...(synced.mediaDeletedFromStorage ? { mediaDeletedFromStorage: true } : {}),
        ...(synced.deletedImageIndices ? { deletedImageIndices: synced.deletedImageIndices } : {}),
        _fromExport: true,
      });
      continue;
    }
    // live wins (freshest receipts) — but NOT for the phone's local-only deletion
    // markers, which Firestore has no idea about. Storage & Data purges and the
    // "media only" clear live in SQLite by design, so they arrive ONLY through the
    // sealed history export. Letting the live doc replace the synced entry wholesale
    // resurrected media the user had deleted, every time the message doc still
    // existed (groups, or anything not yet swept by store-and-forward).
    // 🟥 AND NOT FOR THE DOCUMENT PREVIEW EITHER. The export ships a document's first
    //    page as its own url + already-unsealed key (docThumbUrl / docThumbKey). Live
    //    winning WHOLESALE threw those away — so the moment the phone re-exported a
    //    chat, any document whose live doc carried no thumbnail fields of its own lost
    //    its preview, permanently, with no way to get it back. Carried over only when
    //    the live copy has nothing of its own, so a live thumbnail still wins.
    const carryThumb = (synced && (synced.docThumbUrl || synced.documentThumbUrl)
      && !m.documentThumbUrl && !m.docThumbUrl)
      ? {
        docThumbUrl: synced.docThumbUrl || synced.documentThumbUrl,
        ...(synced.docThumbKey ? { docThumbKey: synced.docThumbKey } : {}),
        ...(synced.documentThumbWidth ? { documentThumbWidth: synced.documentThumbWidth } : {}),
        ...(synced.documentThumbHeight ? { documentThumbHeight: synced.documentThumbHeight } : {}),
      }
      : null;

    if (synced && (synced.mediaDeletedFromStorage || synced.deletedImageIndices || carryThumb)) {
      byId.set(key, {
        ...m,
        ...(carryThumb || {}),
        ...(synced.mediaDeletedFromStorage ? { mediaDeletedFromStorage: true } : {}),
        ...(synced.deletedImageIndices ? { deletedImageIndices: synced.deletedImageIndices } : {}),
      });
    } else {
      byId.set(key, m);
    }
  }
  // same key as the live stream, so a merged history and a live list cannot
  // disagree about where a message sits (see messageOrder.js)
  return orderMessages(Array.from(byId.values()));
}
/** 🟢 A message you JUST sent has `timestamp: serverTimestamp()`, which reads as NULL
 *  locally until the server resolves it. Treating that as 0 sorted brand-new messages
 *  to the very TOP of the conversation (they looked "gone"). Pending = now → bottom. */
export function tsOf(m) {
  const t = m?.timestamp;
  if (!t) return Date.now();
  if (typeof t === 'number') return t;
  if (t.seconds) return t.seconds * 1000;
  if (typeof t.toDate === 'function') { try { return t.toDate().getTime(); } catch { return Date.now(); } }
  const n = new Date(t).getTime();
  return Number.isFinite(n) ? n : Date.now();
}
